/**
 * AccountListScreen
 * Display all accounts with their main goals
 * Allows switching between accounts
 */

import React from 'react';
import {
  View,
  Text,
  StyleSheet,
  FlatList,
  TouchableOpacity,
  SafeAreaView,
} from 'react-native';
import { AccountListScreenProps } from '../navigation/types';
import { useGoals } from '../hooks/useGoals';
import { GoalAccount } from '../models/GoalAccount';

export const AccountListScreen: React.FC<AccountListScreenProps> = ({ navigation }) => {
  const { accounts, currentAccountId, switchAccount, goals } = useGoals();

  const getMainGoalForAccount = (accountId: string): string => {
    const accountGoals = goals.filter(g => g.accountId === accountId && g.type === 'main');
    return accountGoals.length > 0 ? accountGoals[0].name : 'No main goal set';
  };

  const handleAccountPress = async (accountId: string) => {
    await switchAccount(accountId);
    navigation.goBack();
  };

  const handleCreateAccount = () => {
    navigation.navigate('CreateAccount');
  };

  const handleEditAccount = (accountId: string) => {
    navigation.navigate('EditAccount', { accountId });
  };

  const renderAccountRow = ({ item }: { item: GoalAccount }) => {
    const isCurrentAccount = item.id === currentAccountId;
    const mainGoal = isCurrentAccount ? getMainGoalForAccount(item.id) : null;
    const accountThemeColor = item.themeColor || '#007AFF';

    return (
      <View style={[styles.accountRow, isCurrentAccount && [styles.accountRowActive, { borderColor: accountThemeColor }]]}>
        <TouchableOpacity
          style={styles.accountRowTouchable}
          onPress={() => handleAccountPress(item.id)}
          accessibilityLabel={isCurrentAccount && mainGoal ? `${item.name} - ${mainGoal}` : item.name}
          accessibilityHint={isCurrentAccount ? 'Currently active account' : 'Tap to switch to this account'}
        >
          <View style={[styles.colorIndicator, { backgroundColor: accountThemeColor }]} />
          <View style={styles.accountContent}>
            <View style={styles.accountHeader}>
              <Text style={[styles.accountName, isCurrentAccount && [styles.accountNameActive, { color: accountThemeColor }]]}>
                {item.name}
              </Text>
              {isCurrentAccount && (
                <View style={[styles.activeBadge, { backgroundColor: accountThemeColor }]}>
                  <Text style={styles.activeBadgeText}>Active</Text>
                </View>
              )}
            </View>
            {isCurrentAccount && mainGoal && (
              <Text style={[styles.goalText, styles.goalTextActive]}>
                {mainGoal}
              </Text>
            )}
            <Text style={styles.createdText}>
              Created {new Date(item.createdAt).toLocaleDateString()}
            </Text>
          </View>
          <View style={[styles.checkbox, isCurrentAccount && [styles.checkboxActive, { backgroundColor: accountThemeColor, borderColor: accountThemeColor }]]}>
            {isCurrentAccount && <Text style={styles.checkmark}>✓</Text>}
          </View>
        </TouchableOpacity>
        <TouchableOpacity
          style={styles.editButton}
          onPress={() => handleEditAccount(item.id)}
          accessibilityLabel="Edit account"
          accessibilityHint="Edit account name and theme"
        >
          <Text style={[styles.editButtonText, { color: accountThemeColor }]}>Edit</Text>
        </TouchableOpacity>
      </View>
    );
  };

  return (
    <SafeAreaView style={styles.container}>
      <View style={styles.header}>
        <Text style={styles.headerTitle}>Your Journeys</Text>
        <Text style={styles.headerSubtitle}>
          Each journey tracks one main focus area
        </Text>
      </View>

      <FlatList
        data={accounts}
        renderItem={renderAccountRow}
        keyExtractor={item => item.id}
        style={styles.list}
        contentContainerStyle={styles.listContent}
        ListEmptyComponent={
          <View style={styles.emptyContainer}>
            <Text style={styles.emptyText}>No accounts yet</Text>
            <Text style={styles.emptySubtext}>Create your first account to get started</Text>
          </View>
        }
      />

      <View style={styles.footer}>
        <TouchableOpacity
          style={styles.createButton}
          onPress={handleCreateAccount}
        >
          <Text style={styles.createButtonText}>+ Create New Journey</Text>
        </TouchableOpacity>
      </View>
    </SafeAreaView>
  );
};

const styles = StyleSheet.create({
  container: {
    flex: 1,
    backgroundColor: '#f5f5f5',
  },
  header: {
    backgroundColor: '#fff',
    padding: 16,
    borderBottomWidth: 1,
    borderBottomColor: '#eee',
  },
  headerTitle: {
    fontSize: 20,
    fontWeight: '600',
    color: '#333',
    marginBottom: 4,
  },
  headerSubtitle: {
    fontSize: 14,
    color: '#666',
  },
  list: {
    flex: 1,
  },
  listContent: {
    padding: 16,
  },
  accountRow: {
    flexDirection: 'row',
    alignItems: 'center',
    backgroundColor: '#fff',
    borderRadius: 8,
    marginBottom: 12,
    borderWidth: 2,
    borderColor: 'transparent',
    overflow: 'hidden',
  },
  accountRowTouchable: {
    flex: 1,
    flexDirection: 'row',
    alignItems: 'center',
    padding: 16,
  },
  accountRowActive: {
    borderColor: '#007AFF',
    backgroundColor: '#F0F8FF',
  },
  colorIndicator: {
    width: 4,
    height: 48,
    borderRadius: 2,
    marginRight: 12,
  },
  accountContent: {
    flex: 1,
  },
  accountHeader: {
    flexDirection: 'row',
    alignItems: 'center',
    marginBottom: 8,
  },
  accountName: {
    fontSize: 18,
    fontWeight: '600',
    color: '#333',
  },
  accountNameActive: {
    color: '#007AFF',
  },
  activeBadge: {
    backgroundColor: '#007AFF',
    paddingHorizontal: 8,
    paddingVertical: 3,
    borderRadius: 4,
    marginLeft: 8,
  },
  activeBadgeText: {
    fontSize: 11,
    fontWeight: '600',
    color: '#fff',
    textTransform: 'uppercase',
  },
  goalText: {
    fontSize: 15,
    color: '#666',
    marginBottom: 6,
    fontWeight: '500',
  },
  goalTextActive: {
    color: '#005BB5',
  },
  createdText: {
    fontSize: 12,
    color: '#999',
  },
  checkbox: {
    width: 32,
    height: 32,
    borderRadius: 16,
    borderWidth: 2,
    borderColor: '#ddd',
    justifyContent: 'center',
    alignItems: 'center',
    marginLeft: 12,
  },
  checkboxActive: {
    backgroundColor: '#007AFF',
    borderColor: '#007AFF',
  },
  checkmark: {
    fontSize: 18,
    color: '#fff',
    fontWeight: '700',
  },
  emptyContainer: {
    padding: 32,
    alignItems: 'center',
  },
  emptyText: {
    fontSize: 16,
    color: '#666',
    marginBottom: 8,
  },
  emptySubtext: {
    fontSize: 14,
    color: '#999',
    textAlign: 'center',
  },
  footer: {
    padding: 16,
    backgroundColor: '#fff',
    borderTopWidth: 1,
    borderTopColor: '#eee',
  },
  createButton: {
    backgroundColor: '#007AFF',
    padding: 16,
    borderRadius: 8,
    alignItems: 'center',
  },
  createButtonText: {
    fontSize: 16,
    fontWeight: '600',
    color: '#fff',
  },
  editButton: {
    paddingHorizontal: 16,
    paddingVertical: 16,
    justifyContent: 'center',
    borderLeftWidth: 1,
    borderLeftColor: '#eee',
  },
  editButtonText: {
    fontSize: 14,
    fontWeight: '600',
  },
});
